import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimatedBackground from '../components/AnimatedBackground';
import ThemeToggle from '../components/ThemeToggle';
import Footer from '../components/Footer';
import ToastContainer, { useToast } from '../components/ToastContainer';
import { getUser, getAuthToken, saveAuth } from '../services/auth';
import { updateUserProfile } from '../services/api';
import { t, getLanguage } from '../utils/i18n';
import logoImage from '../assets/babysit_logo.png';

const defaultAddress = { street: '', city: '', postalCode: '' };

const stepsForRole = (role) => {
  const common = [
    { id: 'identity', title: 'Osnovni podaci', subtitle: 'Kako da vas oslovljavamo?' },
    { id: 'role', title: 'Tip naloga', subtitle: 'Da prilagodimo iskustvo vama.' },
  ];
  const roleSpecific = role === 'babysitter'
    ? { id: 'details', title: 'Poslovni podaci', subtitle: 'Satnica i dostupnost.' }
    : { id: 'details', title: 'Porodične potrebe', subtitle: 'Da bismo bolje uparili profile.' };
  return [
    ...common,
    roleSpecific,
    { id: 'location', title: 'Lokacija', subtitle: 'Lakše pronalaženje u blizini.' },
    { id: 'review', title: 'Pregled', subtitle: 'Proverite i završite onboarding.' },
  ];
};

export default function CompleteProfile() {
  const navigate = useNavigate();
  const { showToast, toasts, removeToast } = useToast();
  const [user, setUser] = useState(() => getUser());
  const [language, setLanguageState] = useState(getLanguage());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [address, setAddress] = useState(defaultAddress);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    role: 'parent',
    hourlyRate: '',
    childBirthYear: '',
    citiesText: '',
  });
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    const handleLanguageChange = () => setLanguageState(getLanguage());
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    if (!user) {
      showToast(t('auth.completeProfile.sessionExpired', language), 'error', 5000);
      navigate('/login', { replace: true });
      return;
    }
    if (user?.profileComplete) {
      navigate('/dashboard', { replace: true });
      return;
    }
    const cities = user?.preferences?.cities || [];
    setForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || '',
      role: user.role || 'parent',
      hourlyRate: user.hourlyRate ? String(user.hourlyRate) : '',
      childBirthYear: user.preferences?.childBirthYear ? String(user.preferences.childBirthYear) : '',
      citiesText: Array.isArray(cities) ? cities.join(', ') : '',
    });
    if (user.address) {
      setAddress({
        street: user.address.street || '',
        city: user.address.city || (user.address.cities && user.address.cities[0]) || '',
        postalCode: user.address.postalCode || user.address.zip || '',
      });
    }
  }, [user, navigate, showToast, language]);

  const steps = useMemo(() => stepsForRole(form.role), [form.role]);
  const progress = Math.round(((stepIndex + 1) / steps.length) * 100);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const isCurrentStepValid = () => {
    const stepId = steps[stepIndex]?.id;
    if (stepId === 'identity') {
      return Boolean(form.firstName.trim() && form.lastName.trim() && form.phone.trim());
    }
    if (stepId === 'role') {
      return Boolean(form.role);
    }
    return true;
  };

  const handleNext = () => {
    if (!isCurrentStepValid()) {
      showToast('Popunite obavezna polja pre nastavka.', 'warning', 2500);
      return;
    }
    if (stepIndex < steps.length - 1) setStepIndex((prev) => prev + 1);
  };

  const handleBack = () => {
    if (stepIndex > 0) setStepIndex((prev) => prev - 1);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.phone.trim() || !form.role) {
      showToast(t('auth.completeProfile.validationError', language), 'error', 4000);
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        role: form.role,
      };
      if (form.role === 'babysitter' && form.hourlyRate) {
        payload.hourlyRate = Number(form.hourlyRate);
      }
      const cleanAddress = {
        street: address.street.trim(),
        city: address.city.trim(),
        postalCode: address.postalCode.trim(),
      };
      if (cleanAddress.street || cleanAddress.city || cleanAddress.postalCode) {
        payload.address = cleanAddress;
      }
      const cities = form.citiesText
        .split(',')
        .map((city) => city.trim())
        .filter(Boolean);
      const currentPreferences = user?.preferences || {};
      payload.preferences = {
        ...currentPreferences,
        cities,
        childBirthYear: form.childBirthYear ? Number(form.childBirthYear) : currentPreferences.childBirthYear,
        registrationIncomplete: false,
      };

      const response = await updateUserProfile(payload);
      if (!response?.user) {
        showToast(t('auth.completeProfile.error', language), 'error', 5000);
        return;
      }
      const token = getAuthToken();
      if (token) saveAuth(token, response.user, { rememberMe: true });
      setUser(response.user);
      showToast(t('auth.completeProfile.success', language), 'success', 3000);
      setTimeout(() => navigate('/dashboard', { replace: true }), 500);
    } catch (error) {
      const message = error.response?.data?.message || t('auth.completeProfile.error', language);
      showToast(message, 'error', 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-100 via-white to-violet-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-900 relative overflow-hidden">
      <AnimatedBackground />
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="absolute top-4 right-4 z-20"><ThemeToggle /></div>

      <div className="flex-1 flex items-center justify-center px-3 sm:px-4 py-4 sm:py-6 relative z-10">
        <div className="card w-full max-w-2xl p-5 sm:p-8 shadow-xl">
          <div className="text-center mb-6">
            <img src={logoImage} alt="Nannity" className="w-14 h-14 mx-auto mb-3" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{steps[stepIndex]?.title}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">{steps[stepIndex]?.subtitle}</p>
          </div>

          <div className="mb-6">
            <div className="flex justify-between text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1.5">
              <span>Korak {stepIndex + 1} od {steps.length}</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
              <div className="bg-primary-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 animate-fade-in">
            {steps[stepIndex]?.id === 'identity' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input type="text" value={form.firstName} onChange={(e) => updateField('firstName', e.target.value)} className="input-base" placeholder="Ime" required />
                  <input type="text" value={form.lastName} onChange={(e) => updateField('lastName', e.target.value)} className="input-base" placeholder="Prezime" required />
                </div>
                <input type="tel" value={form.phone} onChange={(e) => updateField('phone', e.target.value)} className="input-base" placeholder="Telefon" required />
              </>
            )}

            {steps[stepIndex]?.id === 'role' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button type="button" onClick={() => updateField('role', 'parent')} className={`p-4 rounded-2xl border-2 text-left ${form.role === 'parent' ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">Roditelj</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Tražim dadilju</p>
                </button>
                <button type="button" onClick={() => updateField('role', 'babysitter')} className={`p-4 rounded-2xl border-2 text-left ${form.role === 'babysitter' ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">Dadilja</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Tražim posao</p>
                </button>
              </div>
            )}

            {steps[stepIndex]?.id === 'details' && form.role === 'babysitter' && (
              <input type="number" min="0" value={form.hourlyRate} onChange={(e) => updateField('hourlyRate', e.target.value)} className="input-base" placeholder="Satnica (npr. 800 RSD/h)" />
            )}

            {steps[stepIndex]?.id === 'details' && form.role === 'parent' && (
              <>
                <select value={form.childBirthYear} onChange={(e) => updateField('childBirthYear', e.target.value)} className="input-base">
                  <option value="">Godina rođenja deteta (opciono)</option>
                  {Array.from({ length: 18 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <input type="text" value={form.citiesText} onChange={(e) => updateField('citiesText', e.target.value)} className="input-base" placeholder="Gradovi (npr. Beograd, Novi Sad)" />
              </>
            )}

            {steps[stepIndex]?.id === 'location' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input type="text" value={address.street} onChange={(e) => setAddress((p) => ({ ...p, street: e.target.value }))} className="input-base" placeholder="Ulica" />
                <input type="text" value={address.city} onChange={(e) => setAddress((p) => ({ ...p, city: e.target.value }))} className="input-base" placeholder="Grad" />
                <input type="text" value={address.postalCode} onChange={(e) => setAddress((p) => ({ ...p, postalCode: e.target.value }))} className="input-base" placeholder="Poštanski broj" />
              </div>
            )}

            {steps[stepIndex]?.id === 'review' && (
              <div className="rounded-2xl border border-primary-200 dark:border-primary-800 bg-primary-50/70 dark:bg-primary-900/20 p-4 text-sm text-gray-700 dark:text-gray-200 space-y-2">
                <p><span className="font-semibold">Ime:</span> {form.firstName} {form.lastName}</p>
                <p><span className="font-semibold">Telefon:</span> {form.phone}</p>
                <p><span className="font-semibold">Uloga:</span> {form.role === 'parent' ? 'Roditelj' : 'Dadilja'}</p>
                {form.role === 'babysitter' && form.hourlyRate && <p><span className="font-semibold">Satnica:</span> {form.hourlyRate} RSD/h</p>}
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <button type="button" onClick={handleBack} disabled={stepIndex === 0} className="btn-secondary flex-1 disabled:opacity-50">Nazad</button>
              {stepIndex < steps.length - 1 ? (
                <button type="button" onClick={handleNext} className="btn-primary flex-1">Sledeće</button>
              ) : (
                <button type="submit" disabled={isSubmitting} className="btn-primary flex-1 disabled:opacity-60">
                  {isSubmitting ? 'Čuvanje...' : 'Završi onboarding'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}

